import { ArrowDownCircle, Edit3 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { type LayoutChangeEvent, Pressable, StyleSheet } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Image, View, XStack, YStack } from "tamagui";
import { LinearGradient } from "tamagui/linear-gradient";
import { Body } from "../ui/Body";
import { Label } from "../ui/Label";
import { Separator } from "../ui/Separator";
import { Title } from "../ui/Title";
import {
  EditorRender,
  EditorRenderInline,
  type EditorValue,
} from "../ui/YooptaEditor";
import type PlanModel from "./PlanModel";
import { DEFAULT_COVER_URL } from "./types";

const duration = 500;

export type PlanInfoProps = {
  descriptionYooptaEditor: EditorValue | null;
  coverUrl: string | null;
  backgroundColor: string | null;
  emoji: string;
  title: string;
  titlePlaceholder?: JSX.Element;
};

export const createInfoPropsFromPlan = (plan?: PlanModel): PlanInfoProps => {
  return {
    descriptionYooptaEditor:
      plan?.planInfo.data.descriptionYooptaEditor ?? null,
    coverUrl: plan?.planInfo.data.coverUrl ?? null,
    backgroundColor: plan?.planInfo.data.backgroundColor ?? null,
    emoji: plan?.planInfo.data.emoji ?? "🎉",
    title: plan?.planInfo.data.title ?? "",
  };
};

export const PlanInfoBox = observer(
  ({
    hideEdit,
    info,
    onEditPress,
  }: {
    info: PlanInfoProps;
    hideEdit?: boolean;
    onEditPress?: () => void;
  }) => {
    const [isExpanded, setExpanded] = React.useState(false);

    let coverUrl = info.coverUrl;
    const currentColor = info.backgroundColor;

    if (!coverUrl && !currentColor) {
      // TODO - set another default
      coverUrl = DEFAULT_COVER_URL;
    }

    const DEFAULT_HEIGHT = 175;

    const hasDescription =
      info.descriptionYooptaEditor &&
      !!Object.keys(info.descriptionYooptaEditor).length;

    const [_notExpandedHeight, setNotExpandedHeight] =
      React.useState<number>(0);
    const [expandedHeight, setExpandedHeight] = React.useState<number>(0);

    // For some reason 82
    const isExpandable = expandedHeight > 82;

    const height = useSharedValue<number>(DEFAULT_HEIGHT);
    const fadeOpacity = useSharedValue<number>(0);
    const rotateArrow = useSharedValue<number>(0);

    const animatedStyle = useAnimatedStyle(
      () => ({
        height: withTiming(height.value, { duration }),
        paddingLeft: 12,
        paddingRight: 12,
        overflow: "hidden",
        backgroundColor: currentColor ?? undefined,
        justifyContent: "flex-end",
      }),
      [currentColor],
    );

    const arrowAnimatedStyle = useAnimatedStyle(
      () => ({
        transform: [{ rotate: `${rotateArrow.value}deg` }],
      }),
      [currentColor],
    );

    const animatedFadeStyle = useAnimatedStyle(
      () => ({
        ...StyleSheet.absoluteFillObject,
        backgroundColor: "#000",
        opacity: fadeOpacity.value,
      }),
      [fadeOpacity],
    );

    const handleLayout = (event: LayoutChangeEvent) => {
      const newHeight = event.nativeEvent.layout.height;

      if (isExpanded) {
        fadeOpacity.value = withTiming(0.8, { duration });
        height.value = 24 + DEFAULT_HEIGHT - 79 + newHeight; // Animate to expanded height
        rotateArrow.value = withTiming(180, { duration });
      } else {
        fadeOpacity.value = withTiming(0, { duration });
        height.value = DEFAULT_HEIGHT; // Animate to collapsed height
        rotateArrow.value = withTiming(0, { duration });
      }
    };

    return (
      <Pressable
        disabled={!isExpandable}
        onPress={() => {
          setExpanded(!isExpanded);
        }}
      >
        <Animated.View style={animatedStyle}>
          {!!coverUrl && (
            <Image
              style={StyleSheet.absoluteFill}
              objectFit={"cover"}
              source={{ uri: coverUrl, cache: "force-cache" }}
            />
          )}

          <LinearGradient
            colors={["transparent", "rgba(0,0,0,0.7)"]}
            position={"absolute"}
            bottom={0}
            left={0}
            right={0}
            height={DEFAULT_HEIGHT}
          />

          <Animated.View style={animatedFadeStyle} />

          {!hideEdit && onEditPress && !hasDescription && (
            <Pressable
              style={{
                position: "absolute",
                right: 24,
                top: 24,
              }}
              onPress={onEditPress}
            >
              <XStack gap="$medium" alignItems="center">
                <Label color="white">Set info</Label>
                <Edit3 size={24} color={"white"} />
              </XStack>
            </Pressable>
          )}

          <YStack
            onLayout={handleLayout}
            gap={"$small"}
            position={"absolute"}
            top={hasDescription ? DEFAULT_HEIGHT - 79 : undefined}
            left={0}
            right={0}
            paddingHorizontal={"$larger"}
            marginBottom={hasDescription ? "$medium" : "$large"}
          >
            <XStack gap="$large" justifyContent="space-between">
              <Title color="#fff" numberOfLines={!isExpanded ? 1 : undefined}>
                {info.emoji ? `${info.emoji} ` : ""}
                {info.title ?? info.titlePlaceholder}
              </Title>
              {isExpandable && (
                <Animated.View
                  style={[
                    arrowAnimatedStyle,
                    { marginTop: 5, alignSelf: "flex-start" },
                  ]}
                >
                  <ArrowDownCircle size={24} color={"#fff"} />
                </Animated.View>
              )}
            </XStack>

            {hasDescription && (
              <>
                {!isExpanded && (
                  <View
                    onLayout={(e) => {
                      setNotExpandedHeight(e.nativeEvent.layout.height);
                    }}
                    opacity={isExpanded ? 0 : 1}
                  >
                    <EditorRenderInline
                      numberOfLines={1}
                      value={info.descriptionYooptaEditor}
                      color="#fff"
                    />
                  </View>
                )}
                <YStack
                  gap={"$medium"}
                  onLayout={(e) => {
                    setExpandedHeight(e.nativeEvent.layout.height);
                  }}
                  pointerEvents={!isExpanded ? "none" : undefined}
                  opacity={isExpanded ? 1 : 0}
                >
                  <EditorRender
                    id="description"
                    value={info.descriptionYooptaEditor}
                    color={"#fff"}
                  />

                  {!hideEdit && onEditPress && (
                    <YStack gap="$medium" opacity={0.75}>
                      <Separator noMargins />
                      <Pressable onPress={onEditPress}>
                        <XStack gap="$small" alignItems="center">
                          <Edit3 size={16} color={"white"} />
                          <Body color="white">Edit</Body>
                        </XStack>
                      </Pressable>
                    </YStack>
                  )}
                </YStack>
              </>
            )}
          </YStack>
        </Animated.View>
      </Pressable>
    );
  },
);
